import React, { useState, useEffect, useCallback, useRef } from "react";
import useApi from "@/hooks/useApi";
import useNetSuiteConfigs from "@/hooks/useNetSuiteConfigs";
import SearchBar from "@/components/SearchBar";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import { IconPlus } from "@velaro/velaro-component-lib/";
import { IconCheck } from "@velaro/velaro-component-lib/";
import NetsuitePanel from "./NetSuitePanel";
import { getMetaDataOptions } from "./MetaDataOptions";

// ---------------------------------------------------------------------------
// Guide sub-components
// ---------------------------------------------------------------------------

type GuideSection = "overview" | "agent-panel" | "write-ops" | "workflow-data" | "ai-tools";

const GUIDE_SECTIONS: { id: GuideSection; title: string }[] = [
  { id: "overview",      title: "What is field mapping?" },
  { id: "agent-panel",   title: "Agent CRM panel" },
  { id: "write-ops",     title: "Lead capture & write ops" },
  { id: "workflow-data", title: "HTTP POST / workflows" },
  { id: "ai-tools",      title: "AI tools (no mapping)" },
];

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">
      {children}
    </pre>
  );
}

function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-primary-bg border border-cornflower-blue-200 rounded-lg p-3 mt-3 text-xs text-cornflower-blue-800 leading-relaxed">
      {children}
    </div>
  );
}

function TermBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-3 text-xs text-slate-600 leading-relaxed">
      {children}
    </div>
  );
}

function InlineCode({ children }: { children: string }) {
  return (
    <code className="bg-white px-1 py-0.5 rounded border border-slate-200 text-cornflower-blue-800 font-mono text-[11px]">
      {children}
    </code>
  );
}

function NSGuideOverview() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">What is Field Mapping?</h3>
      <p className="mb-3">
        Field mapping connects NetSuite data to the platform in two directions: <strong>read</strong> (pull NS records into the agent sidebar) and <strong>write</strong> (push conversation data into NS records like leads, cases, and contacts). This is the configuration that makes both directions work.
      </p>
      <p className="mb-3">
        Each mapping panel targets one NetSuite record type (Customer, Contact, Lead, SupportCase, etc.). You define which NS fields are relevant, what to call them, and — for write operations — which conversation variables map into which NS fields.
      </p>

      <h4 className="font-medium text-slate-700 mt-4 mb-2">What does mapping enable?</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden text-xs">
        <table className="w-full">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Use case</th>
              <th className="px-3 py-2 text-left">Field mapping needed?</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr>
              <td className="px-3 py-2">Show NS customer/order data in the agent sidebar</td>
              <td className="px-3 py-2 text-success-green-700 font-medium">Yes — required</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2">Create a Lead or Contact in NS from a conversation</td>
              <td className="px-3 py-2 text-success-green-700 font-medium">Yes — maps form fields → NS fields</td>
            </tr>
            <tr>
              <td className="px-3 py-2">Create a Support Case in NS from a chat</td>
              <td className="px-3 py-2 text-success-green-700 font-medium">Yes — maps conversation data → NS case fields</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2">Pass NS data into an HTTP POST workflow node</td>
              <td className="px-3 py-2 text-success-green-700 font-medium">Yes — required</td>
            </tr>
            <tr>
              <td className="px-3 py-2">AI answers visitor questions using NS data</td>
              <td className="px-3 py-2 text-slate-500 font-medium">No — AI reads raw data</td>
            </tr>
          </tbody>
        </table>
      </div>

      <TermBox>
        <strong>Record type</strong> — Each mapping panel targets one NetSuite record type (e.g. Customer, Lead, SupportCase). You can add multiple panels to cover different record types in the same integration config.
      </TermBox>
    </div>
  );
}

function NSGuideAgentPanel() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">Agent CRM Panel</h3>
      <p className="mb-3">
        When an agent opens a conversation, the right-side CRM panel pulls matching NetSuite records and displays the fields you've mapped. Field mapping controls exactly which fields appear, their labels, and whether agents can search on them.
      </p>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Field options explained</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden text-xs">
        <table className="w-full">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Option</th>
              <th className="px-3 py-2 text-left">Effect</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr>
              <td className="px-3 py-2 font-medium">Label</td>
              <td className="px-3 py-2">The display name agents see. Rename <InlineCode>custentity_acv</InlineCode> to "Annual Contract Value".</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2 font-medium">Show in search</td>
              <td className="px-3 py-2">Agents can search conversations by this field value (e.g. find chats by NS account number).</td>
            </tr>
            <tr>
              <td className="px-3 py-2 font-medium">Show when selected</td>
              <td className="px-3 py-2">Field appears in the contact detail view when the agent drills into the NS record.</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2 font-medium">Linked to</td>
              <td className="px-3 py-2">For write operations — which conversation variable populates this NS field on record creation.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <InfoBox>
        <strong>Tip:</strong> NetSuite records can have hundreds of fields. Map only what agents need to take action — status, tier, account owner, open cases. A focused sidebar is faster to use than a complete NS record dump.
      </InfoBox>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Example: Customer panel mapping</h4>
      <CodeBlock>{`NS Field                   Label                  Search  Show
────────────────────────────────────────────────────────────────
entityid                   Account Number         ✓       ✓
companyname                Company                ✓       ✓
email                      Email                  ✓       ✓
custentity_tier            Customer Tier                  ✓
custentity_acv             Annual Value                   ✓
status                     NS Status                      ✓`}</CodeBlock>
    </div>
  );
}

function NSGuideWriteOps() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">Lead Capture &amp; Write Operations</h3>
      <p className="mb-3">
        NetSuite integration is read/write. Beyond displaying NS data to agents, you can <strong>create and update NS records directly from a conversation</strong> — leads from pre-chat forms, support cases from chat transcripts, contacts from collected visitor info.
      </p>

      <h4 className="font-medium text-slate-700 mt-3 mb-1">How write mapping works</h4>
      <p className="text-xs text-slate-600 mb-2">
        The <InlineCode>Linked to</InlineCode> field on each mapping row tells the platform which conversation variable to write into that NS field when creating a record. For example, linking NS <InlineCode>email</InlineCode> → <InlineCode>{"{visitor.email}"}</InlineCode> means the visitor's collected email gets written to the NS record's email field.
      </p>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Example: Lead creation mapping</h4>
      <p className="text-xs text-slate-600 mb-1">Map these NS Lead fields to capture visitor data from a pre-chat form:</p>
      <CodeBlock>{`NS Field        Linked to (conversation variable)
────────────────────────────────────────────────
firstname       {visitor.firstName}
lastname        {visitor.lastName}
email           {visitor.email}
phone           {visitor.phone}
leadsource      "Live Chat"  (static default)
comments        {conversation.summary}`}</CodeBlock>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Example: Support case creation</h4>
      <p className="text-xs text-slate-600 mb-1">Create a NS SupportCase at the end of a conversation:</p>
      <CodeBlock>{`NS Field              Linked to
────────────────────────────────────────────────
title                 {conversation.firstMessage}
incomingmessage       {conversation.transcript}
status                "Open"
startdate             {conversation.startedAt}
custbody_channel      "Live Chat"
custevent_agent       {agent.name}`}</CodeBlock>

      <InfoBox>
        <strong>Custom field IDs:</strong> NS custom fields have IDs like <InlineCode>custentity_acv</InlineCode> or <InlineCode>custbody_channel</InlineCode>. These are specific to your NetSuite account. Get them from NetSuite &gt; Customization &gt; Lists, Records, &amp; Fields &gt; [Record Type] &gt; Custom Fields.
      </InfoBox>

      <TermBox>
        <strong>Static defaults</strong> — when a field should always be the same value (like <InlineCode>leadsource = "Live Chat"</InlineCode>), set it as the Default Value rather than linking to a variable. It will be written as-is on every record creation.
      </TermBox>
    </div>
  );
}

function NSGuideWorkflowData() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">HTTP POST / Workflow Data Passing</h3>
      <p className="mb-3">
        In a workflow, an <strong>HTTP POST node</strong> can call an external API or webhook and include NetSuite data in the request body. To include a NS field value in a POST, you first map it — that gives it a variable name — then reference that variable in the POST node.
      </p>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">How the variable is formed</h4>
      <p className="text-xs text-slate-600 mb-2">
        Mapped NS fields become available as workflow variables using the pattern <InlineCode>{"{netsuite.recordType.fieldName}"}</InlineCode>:
      </p>
      <CodeBlock>{`Record: Customer  →  Field: custentity_tier
Variable: {netsuite.customer.custentity_tier}

Record: Contact  →  Field: email
Variable: {netsuite.contact.email}`}</CodeBlock>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Example: Notify a downstream system</h4>
      <p className="text-xs text-slate-600 mb-1">
        Send a webhook after a chat ends, including the visitor's NS account tier and open case count:
      </p>
      <CodeBlock>{`// HTTP POST node — request body
{
  "visitor_email":   "{visitor.email}",
  "ns_account_id":   "{netsuite.customer.entityid}",
  "account_tier":    "{netsuite.customer.custentity_tier}",
  "open_cases":      "{netsuite.customer.custentity_opencases}",
  "conversation_id": "{conversation.id}",
  "agent":           "{agent.name}"
}`}</CodeBlock>

      <InfoBox>
        <strong>Map first, then reference.</strong> A NS field only becomes a workflow variable after you add a mapping entry for it. If <InlineCode>{"{netsuite.customer.custentity_tier}"}</InlineCode> is empty in a POST body, check that <InlineCode>custentity_tier</InlineCode> has a mapping row for the Customer record type.
      </InfoBox>
    </div>
  );
}

function NSGuideAiTools() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">AI Tools — No Field Mapping Required</h3>
      <p className="mb-3">
        NetSuite AI tools (read-only queries like "look up customer by email", "get open cases for this account") work completely separately from field mapping. They query the NS REST API at runtime, receive the full raw JSON response, and the AI interprets it — no mapping configuration needed.
      </p>

      <h4 className="font-medium text-slate-700 mt-3 mb-1">How it works</h4>
      <p className="text-xs text-slate-600 mb-2">
        When an AI workflow node runs a NS query, it gets back the raw record with all its fields. The AI then uses natural language to extract what's relevant to the visitor's question and compose a response — it doesn't need you to pre-define which fields matter.
      </p>
      <CodeBlock>{`// AI query: "What is the support tier for this customer?"
// AI receives raw NS Customer record (all fields)
// AI responds to visitor:
"Your account is on our Enterprise tier with a dedicated
 support engineer. Your current SLA is 4-hour response
 for critical issues."`}</CodeBlock>

      <InfoBox>
        <strong>Key distinction:</strong> Field mapping is a <em>static display config</em> — you decide once what appears in the sidebar. AI tools are <em>dynamic runtime queries</em> — the AI picks what's relevant based on the question. You don't need to map a field for the AI to use it.
      </InfoBox>

      <TermBox>
        <strong>When you need both:</strong> Use AI tools for visitor-facing conversational answers. Use field mapping for the agent sidebar display, for creating NS records from conversations, and for injecting NS data into HTTP POST workflow nodes. These are independent features — configure each where needed.
      </TermBox>
    </div>
  );
}

function FieldMappingGuide() {
  const [guideSection, setGuideSection] = useState<GuideSection>("overview");
  return (
    <div className="bg-white rounded-lg border border-slate-200 mb-4">
      <div className="flex">
        {/* Sidebar nav */}
        <nav className="w-52 shrink-0 border-r border-slate-200 p-3" aria-label="Field mapping guide sections">
          <div className="space-y-0.5">
            {GUIDE_SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setGuideSection(s.id)}
                className={`block w-full text-left px-3 py-1.5 rounded text-xs transition-colors ${
                  guideSection === s.id
                    ? "bg-primary-bg text-primary-active font-medium"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {s.title}
              </button>
            ))}
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 p-5 text-sm text-slate-700 leading-relaxed overflow-y-auto max-h-[520px]">
          {guideSection === "overview"      && <NSGuideOverview />}
          {guideSection === "agent-panel"   && <NSGuideAgentPanel />}
          {guideSection === "write-ops"     && <NSGuideWriteOps />}
          {guideSection === "workflow-data" && <NSGuideWorkflowData />}
          {guideSection === "ai-tools"      && <NSGuideAiTools />}
        </div>
      </div>
    </div>
  );
}

const handleSearch = (searchText: string) => {};

export interface NetSuiteMap {
  id: number;
  netSuiteField: any;
  label: string;
  linkedTo: string;
  create: boolean;
  usedForSearching: boolean;
  showWhenSelected: boolean;
  showInSearch: boolean;
  control: string;
  defaultValue: string;
  recordType: string;
}

interface Props {
  teamId: number;
  selectedConfigId: number;
}

export default function NetSuiteDataMapping(props: Props) {
  const [panels, setPanels] = useState<{ id: number; metadataType: string }[]>(
    []
  );
  const [showSaved, setShowSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [mappedFields, setMappedFields] = useState<any[]>([]);
  const [lastAddedPanelId, setLastAddedPanelId] = useState<number | null>(null);
  const panelRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const api = useApi();
  const { items, loading: configLoading } = useNetSuiteConfigs(props.teamId);
  const metaDataOptions = getMetaDataOptions();

  const addPanel = () => {
    const newPanelId = panels.length + 1;
    setPanels([...panels, { id: newPanelId, metadataType: "" }]);
    setLastAddedPanelId(newPanelId);
  };

  const showSavedMessage = () => {
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 3000);
  };

  const fetchMappingFieldCount = useCallback(
    async (metadataType: string) => {
      if (props.selectedConfigId === 0) return { metadataType, dataLength: 0 };

      try {
        const resp = await api.messaging.get(
          `NetSuite/Integrations/Mapping/${metadataType}/${props.selectedConfigId}`
        );
        if (!resp.ok) throw new Error("Failed to fetch data");

        const json = await resp.json();
        return {
          metadataType,
          dataLength: Array.isArray(json) ? json.length : 0
        };
      } catch (error) {
        console.error(`Error fetching mapping for ${metadataType}:`, error);
        return { metadataType, dataLength: 0 };
      }
    },
    [api.messaging, props.selectedConfigId]
  );

  const fetchAllMappings = async () => {
    setPanels([]);
    setLoading(true);

    const panelPromises = metaDataOptions
      .filter((option) => option !== "")
      .map(fetchMappingFieldCount);

    const results = await Promise.all(panelPromises);

    const panelsToAdd = results
      .filter(({ dataLength }) => dataLength > 0)
      .map(({ metadataType }, index) => ({ id: index + 1, metadataType }));

    setPanels(panelsToAdd);
    setLoading(false);
  };

  useEffect(() => {
    if (props.selectedConfigId != undefined && props.selectedConfigId > 0) {
      fetchAllMappings();
    }
  }, [props.selectedConfigId]);

  useEffect(() => {
    showSavedMessage();
  }, []);

  useEffect(() => {
    if (lastAddedPanelId && panelRefs.current[lastAddedPanelId]) {
      const panelElement = panelRefs.current[lastAddedPanelId];
      if (panelElement) {
        panelElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
        setLastAddedPanelId(null);
      }
    }
  }, [lastAddedPanelId, panels]);

  return (
    <div>
      {/* Guide header row */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs text-slate-500">
            Configure which NetSuite fields appear in the agent CRM sidebar, and map conversation data to NS record fields for lead capture and case creation.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowGuide(!showGuide)}
          aria-expanded={showGuide}
          aria-controls="ns-mapping-guide"
          className="shrink-0 px-3 py-1.5 text-xs font-medium text-primary bg-primary-bg rounded-lg hover:bg-primary-bg transition-colors focus:outline-none focus:ring-1 focus:ring-primary/30"
        >
          {showGuide ? "Hide Guide" : "Field Mapping Guide ▾"}
        </button>
      </div>

      {showGuide && (
        <div id="ns-mapping-guide">
          <FieldMappingGuide />
        </div>
      )}

      <div className="justify-between items-center inline-flex w-full">
        <div className="justify-start items-start flex">
          <SearchBar placeholderText="Search" onSearch={handleSearch} />
        </div>

        <div className="justify-end items-center gap-3 flex w-full">
          {showSaved && (
            <div className="flex items-center gap-2 text-slate-500">
              <span className="filter invert">
                {React.createElement(IconCheck, {
                  className: "text-slate-500"
                })}
              </span>
              <div className="text-slate-500 text-sm font-medium">
                All changes saved
              </div>
            </div>
          )}
          <PrimaryBlueButton
            label="Add Mapping"
            onClick={addPanel}
            className="font-medium h-full"
            prefixIcon={React.createElement(IconPlus)}
          />
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center my-4">
          <span>Loading mappings...</span>
        </div>
      )}

      <div className="mt-4">
        {panels.map((panel) => (
          <div
            key={panel.id}
            ref={(el) => {
              panelRefs.current[panel.id] = el;
            }}
          >
            <NetsuitePanel
              teamId={props.teamId!}
              metadataType={panel.metadataType}
              showSavedMessage={showSavedMessage}
              selectedConfigId={props.selectedConfigId}
              isDeleteDisabled={panels.length === 1}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
